import React, { useState } from 'react';

const AboutTab = ({ projectInfo, dataSources }) => {
  const [appendicesExpanded, setAppendicesExpanded] = useState(false);

  const toggleAppendices = () => {
    setAppendicesExpanded(!appendicesExpanded);
  };

  return (
    <div className="space-y-6">
      {/* About This Project Section */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">About This Project</h3>
        <div className="prose max-w-none">
          <p className="text-gray-600 mb-6">{projectInfo.overview}</p>
          <h4 className="text-gray-900 font-medium mb-4">Project Overview Video</h4>
          <div className="relative w-full mb-6" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/TKD7eSkDSGI"
              title="Exploring Homelessness in San Diego"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h5 className="text-gray-900 font-medium mb-2">Key Research Questions:</h5>
              <ul className="text-gray-600 space-y-2">
                <li>Are crime rates correlated with areas of dense homeless populations?</li>
                <li>Which types of crimes show significant patterns in relation to homeless encampments?</li>
                <li>How do changes in unemployment and housing affordability impact homelessness trends?</li>
                <li>What role can data play in developing targeted solutions?</li>
              </ul>
            </div>
            <h4 className="text-gray-900 font-medium mt-6">Methodology</h4>
            <p className="text-gray-600">
              {projectInfo.methodology}
            </p>
            <p className="text-gray-600">
              The ultimate goal is to translate these insights into actionable strategies that can address the root causes of
              homelessness and create lasting positive change in our communities.
            </p>
          </div>
        </div>
      </div>

      {/* Data Sources Section */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Data Sources</h3>
        <p className="text-gray-600 mb-6">{dataSources.description}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dataSources.sources.map((source, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">{source.name}</h4>
              <div className="space-y-1 text-sm">
                <p className="text-gray-600">Type: {source.type}</p>
                <p className="text-gray-600">Data: {source.dataType}</p>
                <p className="text-gray-600">Updates: {source.frequency}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Appendices Section */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        {/* Appendices Header */}
        <div className="flex items-center justify-between cursor-pointer" onClick={toggleAppendices}>
          <h3 className="text-lg font-semibold text-gray-900">Appendices</h3>
          <svg
            className={`w-6 h-6 transform transition-transform duration-200 ${
              appendicesExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {appendicesExpanded && (
          <div>
            {/* Appendix Table 1 */}
            <div className="mt-4 mb-8">
              <h4 className="text-md font-bold italic mb-4 text-gray-800">
                Appendix Table 1: Areas where Homeless Encampments were Reported
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-[#535fc1]">
                    <tr>
                      <th className="px-4 py-2 border-b text-white">ZIP Code</th>
                      <th className="px-4 py-2 border-b text-white">Areas Covered</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appendixTable1Data.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border-b">{item.zipCode}</td>
                        <td className="px-4 py-2 border-b">{item.areasCovered}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Appendix Table 2 */}
            <div className="mb-8">
              <h4 className="text-md font-bold italic mb-4 text-gray-800">
                Appendix Table 2: Crimes Against Persons Breakdown
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-[#535fc1]">
                    <tr>
                      <th className="px-4 py-2 border-b text-white">Crimes Against Persons</th>
                      <th className="px-4 py-2 border-b text-white">Offense Count</th>
                      <th className="px-4 py-2 border-b text-white">Percentage of Total Offenses</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appendixTable2Data.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.offense}
                        </td>
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.count}
                        </td>
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.percentage}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Appendix Table 3 */}
            <div className="mb-8">
              <h4 className="text-md font-bold italic mb-4 text-gray-800">
                Appendix Table 3: Crimes Against Property Breakdown
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-[#535fc1]">
                    <tr>
                      <th className="px-4 py-2 border-b text-white">Crimes Against Property</th>
                      <th className="px-4 py-2 border-b text-white">Offense Count</th>
                      <th className="px-4 py-2 border-b text-white">Percentage of Total Offenses</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appendixTable3Data.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.offense}
                        </td>
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.count}
                        </td>
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.percentage}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Appendix Table 4 */}
            <div className="mb-8">
              <h4 className="text-md font-bold italic mb-4 text-gray-800">
                Appendix Table 4: Crimes Against Society Breakdown
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-[#535fc1]">
                    <tr>
                      <th className="px-4 py-2 border-b text-white">Crimes Against Society</th>
                      <th className="px-4 py-2 border-b text-white">Offense Count</th>
                      <th className="px-4 py-2 border-b text-white">Percentage of Total Offenses</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appendixTable4Data.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.offense}
                        </td>
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.count}
                        </td>
                        <td className={`px-4 py-2 border-b ${item.isBold ? 'font-bold' : ''}`}>
                          {item.percentage}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Appendix Table 5 */}
            <div>
              <h4 className="text-md font-bold italic mb-4 text-gray-800">
                Appendix Table 5: Most Prominent and Total Offense by Zip Code
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-[#535fc1]">
                    <tr>
                      <th className="px-4 py-2 border-b text-white">CIBRS Offense Description</th>
                      <th className="px-4 py-2 border-b text-white">Zip Code</th>
                      <th className="px-4 py-2 border-b text-white">Highest Offense Count</th>
                      <th className="px-4 py-2 border-b text-white">Total Offense Count</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appendixTable5Data.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border-b">{item.offenseDescription}</td>
                        <td className="px-4 py-2 border-b">{item.zipCode}</td>
                        <td className="px-4 py-2 border-b">{item.highestOffenseCount}</td>
                        <td className="px-4 py-2 border-b">{item.totalOffenseCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

// Data for Appendix Tables
const appendixTable1Data = [
  { zipCode: '91911', areasCovered: 'Chula Vista, Otay, Sunbow, Otay Ranch' },
  { zipCode: '91942', areasCovered: 'La Mesa, Lake Murray, Grossmont' },
  { zipCode: '92014', areasCovered: 'Del Mar, Del Mar Heights' },
  { zipCode: '92025', areasCovered: 'Escondido, South Escondido, Midway' },
  { zipCode: '92037', areasCovered: 'La Jolla, La Jolla Shores, Torrey Pines, Bird Rock' },
  { zipCode: '92071', areasCovered: 'Santee, Carlton Hills, Mission Creek' },
  { zipCode: '92075', areasCovered: 'Solana Beach, Lomas Santa Fe, Eden Gardens' },
  { zipCode: '92093', areasCovered: 'UC San Diego Campus' },
  { zipCode: '92101', areasCovered: 'Downtown San Diego, Little Italy, East Village, Marina District' },
  { zipCode: '92102', areasCovered: 'Golden Hill, Grant Hill, Stockton, Mount Hope' },
  { zipCode: '92103', areasCovered: 'Hillcrest, Mission Hills, Bankers Hill' },
  { zipCode: '92104', areasCovered: 'North Park, South Park, Burlingame' },
  { zipCode: '92105', areasCovered: 'City Heights, Teralta, Swan Canyon, Fairmount Village' },
  { zipCode: '92106', areasCovered: 'Point Loma, Roseville-Fleetridge, La Playa, Wooded Area' },
  { zipCode: '92107', areasCovered: 'Ocean Beach, Sunset Cliffs' },
  { zipCode: '92108', areasCovered: 'Mission Valley, Civita, Hazard Center' },
  { zipCode: '92109', areasCovered: 'Pacific Beach, Mission Beach, Crown Point' },
  { zipCode: '92110', areasCovered: 'Old Town, Morena, Midway District' },
  { zipCode: '92111', areasCovered: 'Linda Vista, Kearny Mesa' },
  { zipCode: '92113', areasCovered: 'Logan Heights, Barrio Logan, Shelltown' },
  { zipCode: '92114', areasCovered: 'Encanto, Paradise Hills, Skyline, Jamacha-Lomita' },
  { zipCode: '92115', areasCovered: 'College Area, Rolando, El Cerrito, Talmadge' },
  { zipCode: '92116', areasCovered: 'Normal Heights, Kensington, Adams North' },
  { zipCode: '92117', areasCovered: 'Clairemont Mesa, Bay Ho, Bay Park, Clairemont Mesa East' },
  { zipCode: '92118', areasCovered: 'Coronado, Coronado Cays' },
  { zipCode: '92119', areasCovered: 'San Carlos, Lake Murray' },
  { zipCode: '92120', areasCovered: 'Allied Gardens, Del Cerro, Grantville' },
  { zipCode: '92121', areasCovered: 'Sorrento Valley, Torrey Pines' },
  { zipCode: '92122', areasCovered: 'University City, UTC' },
  { zipCode: '92123', areasCovered: 'Serra Mesa, Birdland' },
  { zipCode: '92124', areasCovered: 'Tierrasanta' },
  { zipCode: '92126', areasCovered: 'Mira Mesa, Sorrento Mesa' },
  { zipCode: '92127', areasCovered: 'Rancho Bernardo, 4S Ranch, Del Sur, Black Mountain Ranch' },
  { zipCode: '92128', areasCovered: 'Rancho Bernardo, Carmel Mountain Ranch, Sabre Springs' },
  { zipCode: '92129', areasCovered: 'Rancho Peñasquitos, Torrey Highlands' },
  { zipCode: '92130', areasCovered: 'Carmel Valley, Pacific Highlands Ranch, Fairbanks Ranch' },
  { zipCode: '92131', areasCovered: 'Scripps Ranch' },
  { zipCode: '92136', areasCovered: 'Naval Base San Diego' },
  { zipCode: '92139', areasCovered: 'Paradise Hills' },
  { zipCode: '92140', areasCovered: 'Marine Corps Recruit Depot' },
  { zipCode: '92145', areasCovered: 'Marine Corps Air Station Miramar' },
  { zipCode: '92154', areasCovered: 'Otay Mesa, Nestor, Palm City, Egger Highlands, parts of Imperial Beach' },
  { zipCode: '92161', areasCovered: 'VA Medical Center' },
  { zipCode: '92173', areasCovered: 'San Ysidro, Border Area' },
];

const appendixTable2Data = [
  { offense: 'Murder', count: '46', percentage: '<1%' },
  { offense: 'Negligent Manslaughter', count: '16', percentage: '<1%' },
  { offense: 'Justifiable Homicide', count: '4', percentage: '<1%' },
  { offense: 'Non-consensual Sex Offenses', count: '844', percentage: '5%', isBold: true },
  { offense: 'Rape', count: '274', percentage: '2%' },
  { offense: 'Sodomy', count: '62', percentage: '<1%' },
  { offense: 'Sexual Assault with an Object', count: '0', percentage: '–' },
  { offense: 'Fondling', count: '508', percentage: '3%' },
  { offense: 'Aggravated Assault', count: '5,412', percentage: '32%' },
  { offense: 'Simple Assault', count: '9,309', percentage: '55%' },
  { offense: 'Intimidation', count: '1,170', percentage: '7%' },
  { offense: 'Kidnapping/Abduction', count: '0', percentage: '–' },
  { offense: 'Consensual Sex Offenses', count: '32', percentage: '<1%', isBold: true },
  { offense: 'Incest', count: '1', percentage: '<1%' },
  { offense: 'Statutory Rape', count: '31', percentage: '<1%' },
  { offense: 'Human Trafficking, Commercial Sex Acts', count: '0', percentage: '–' },
  { offense: 'Human Trafficking, Involuntary Servitude', count: '0', percentage: '–' },
  { offense: 'Total Crimes Against Persons', count: '16,833', percentage: '100%', isBold: true },
];

const appendixTable3Data = [
  { offense: 'Robbery', count: '1,475', percentage: '4%' },
  { offense: 'Burglary/Breaking & Entering', count: '3,528', percentage: '10%' },
  { offense: 'Motor Vehicle Theft', count: '7,509', percentage: '21%' },
  { offense: 'Arson', count: '172', percentage: '<1%' },
  { offense: 'Destruction Of Property', count: '0', percentage: '0%' },
  { offense: 'Counterfeiting/Forgery', count: '280', percentage: '1%' },
  { offense: 'Embezzlement', count: '99', percentage: '<1%' },
  { offense: 'Extortion/Blackmail', count: '17', percentage: '<1%' },
  { offense: 'Bribery', count: '0', percentage: '–' },
  { offense: 'Stolen Property Offenses', count: '794', percentage: '2%' },
  { offense: 'Larceny/Theft Offenses', count: '18,671', percentage: '52%', isBold: true },
  { offense: 'Pocket-picking', count: '67', percentage: '<1%' },
  { offense: 'Purse-snatching', count: '87', percentage: '<1%' },
  { offense: 'Shoplifting', count: '2,167', percentage: '6%' },
  { offense: 'Theft From Building', count: '685', percentage: '2%' },
  { offense: 'Theft From Coin Operated Machine or Device', count: '9', percentage: '<1%' },
  { offense: 'Theft From Motor Vehicle', count: '6,297', percentage: '17%' },
  { offense: 'Theft of Motor Vehicle Parts/Accessories', count: '946', percentage: '3%' },
  { offense: 'All Other Larceny', count: '8,413', percentage: '23%' },
  { offense: 'Fraud Offenses', count: '3,510', percentage: '10%', isBold: true },
  { offense: 'False Pretenses/Swindle/Confidence Game', count: '446', percentage: '1%' },
  { offense: 'Credit Card/Automatic Teller Fraud', count: '328', percentage: '1%' },
  { offense: 'Impersonation', count: '2,008', percentage: '6%' },
  { offense: 'Welfare Fraud', count: '2', percentage: '<1%' },
  { offense: 'Wire Fraud', count: '0', percentage: '–' },
  { offense: 'Identity Theft', count: '715', percentage: '2%' },
  { offense: 'Hacking/Computer Invasion', count: '11', percentage: '<1%' },
  { offense: 'Total Crimes Against Property', count: '36,055', percentage: '100%', isBold: true },
];

const appendixTable4Data = [
  { offense: 'Drug/Narcotic Violations', count: '5,412', percentage: '53%' },
  { offense: 'Drug Equipment Violations', count: '3,246', percentage: '32%' },
  { offense: 'Pornography/Obscene Material', count: '22', percentage: '<1%' },
  { offense: 'Prostitution', count: '88', percentage: '1%' },
  { offense: 'Weapons Law Violation', count: '1,373', percentage: '14%' },
  { offense: 'Animal Cruelty', count: '2', percentage: '<1%' },
  { offense: 'Gambling Offenses', count: '7', percentage: '<1%', isBold: true },
  { offense: 'Betting/Wagering', count: '7', percentage: '<1%' },
  { offense: 'Operating/Promoting/Assisting Gambling', count: '0', percentage: '–' },
  { offense: 'Gambling Equipment Violations', count: '0', percentage: '–' },
  { offense: 'Sports Tampering', count: '0', percentage: '–' },
  { offense: 'Total Crimes Against Society', count: '10,150', percentage: '100%', isBold: true },
];

const appendixTable5Data = [
  { offenseDescription: 'Motor Vehicle Theft', zipCode: '91911', highestOffenseCount: '472', totalOffenseCount: '3,612' },
  { offenseDescription: 'Simple Assault', zipCode: '91942', highestOffenseCount: '331', totalOffenseCount: '2,188' },
  { offenseDescription: 'Simple Assault', zipCode: '92014', highestOffenseCount: '55', totalOffenseCount: '399' },
  { offenseDescription: 'Drug/Narcotic Violations', zipCode: '92025', highestOffenseCount: '607', totalOffenseCount: '3,141' },
  { offenseDescription: 'All Other Larceny', zipCode: '92037', highestOffenseCount: '255', totalOffenseCount: '1,217' },
  { offenseDescription: 'Simple Assault', zipCode: '92071', highestOffenseCount: '418', totalOffenseCount: '2,016' },
  { offenseDescription: 'Burglary/Breaking & Entering', zipCode: '92075', highestOffenseCount: '57', totalOffenseCount: '397' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92093', highestOffenseCount: '18', totalOffenseCount: '56' },
  { offenseDescription: 'Simple Assault', zipCode: '92101', highestOffenseCount: '1,689', totalOffenseCount: '9,883' },
  { offenseDescription: 'Simple Assault', zipCode: '92102', highestOffenseCount: '281', totalOffenseCount: '1,841' },
  { offenseDescription: 'All Other Larceny', zipCode: '92103', highestOffenseCount: '332', totalOffenseCount: '2,047' },
  { offenseDescription: 'Destruction of Property', zipCode: '92104', highestOffenseCount: '320', totalOffenseCount: '1,961' },
  { offenseDescription: 'Destruction of Property', zipCode: '92105', highestOffenseCount: '430', totalOffenseCount: '2,892' },
  { offenseDescription: 'Simple Assault', zipCode: '92106', highestOffenseCount: '98', totalOffenseCount: '594' },
  { offenseDescription: 'Simple Assault', zipCode: '92107', highestOffenseCount: '195', totalOffenseCount: '1,264' },
  { offenseDescription: 'All Other Larceny', zipCode: '92108', highestOffenseCount: '863', totalOffenseCount: '3,573' },
  { offenseDescription: 'All Other Larceny', zipCode: '92109', highestOffenseCount: '395', totalOffenseCount: '2,748' },
  { offenseDescription: 'All Other Larceny', zipCode: '92110', highestOffenseCount: '480', totalOffenseCount: '3,305' },
  { offenseDescription: 'Destruction of Property', zipCode: '92111', highestOffenseCount: '221', totalOffenseCount: '1,687' },
  { offenseDescription: 'Simple Assault', zipCode: '92113', highestOffenseCount: '474', totalOffenseCount: '3,010' },
  { offenseDescription: 'Simple Assault', zipCode: '92114', highestOffenseCount: '238', totalOffenseCount: '1,611' },
  { offenseDescription: 'Destruction of Property', zipCode: '92115', highestOffenseCount: '386', totalOffenseCount: '2,723' },
  { offenseDescription: 'Destruction of Property', zipCode: '92116', highestOffenseCount: '156', totalOffenseCount: '958' },
  { offenseDescription: 'All Other Larceny', zipCode: '92117', highestOffenseCount: '271', totalOffenseCount: '1,565' },
  { offenseDescription: 'All Other Larceny', zipCode: '92118', highestOffenseCount: '1', totalOffenseCount: '3' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92119', highestOffenseCount: '62', totalOffenseCount: '376' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92120', highestOffenseCount: '136', totalOffenseCount: '1,000' },
  { offenseDescription: 'Burglary/Breaking & Entering', zipCode: '92121', highestOffenseCount: '87', totalOffenseCount: '508' },
  { offenseDescription: 'All Other Larceny', zipCode: '92122', highestOffenseCount: '276', totalOffenseCount: '1,302' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92123', highestOffenseCount: '242', totalOffenseCount: '1,312' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92124', highestOffenseCount: '94', totalOffenseCount: '549' },
  { offenseDescription: 'Destruction of Property', zipCode: '92126', highestOffenseCount: '288', totalOffenseCount: '1,748' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92127', highestOffenseCount: '102', totalOffenseCount: '603' },
  { offenseDescription: 'All Other Larceny', zipCode: '92128', highestOffenseCount: '153', totalOffenseCount: '855' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92129', highestOffenseCount: '126', totalOffenseCount: '696' },
  { offenseDescription: 'All Other Larceny', zipCode: '92130', highestOffenseCount: '161', totalOffenseCount: '783' },
  { offenseDescription: 'Theft From Motor Vehicle', zipCode: '92131', highestOffenseCount: '82', totalOffenseCount: '415' },
  { offenseDescription: 'Motor Vehicle Theft', zipCode: '92136', highestOffenseCount: '10', totalOffenseCount: '48' },
  { offenseDescription: 'Simple Assault', zipCode: '92139', highestOffenseCount: '116', totalOffenseCount: '634' },
  { offenseDescription: 'Burglary/Breaking & Entering', zipCode: '92140', highestOffenseCount: '2', totalOffenseCount: '15' },
  { offenseDescription: 'Theft From Building', zipCode: '92145', highestOffenseCount: '3', totalOffenseCount: '17' },
  { offenseDescription: 'Motor Vehicle Theft', zipCode: '92154', highestOffenseCount: '1,104', totalOffenseCount: '3,364' },
  { offenseDescription: 'Aggravated Assault', zipCode: '92161', highestOffenseCount: '2', totalOffenseCount: '6' },
  { offenseDescription: 'All Other Larceny', zipCode: '92173', highestOffenseCount: '586', totalOffenseCount: '2,502' },
];

export default AboutTab;
